import { TextFieldVariants } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'

type DateProps = {
  label: string
  disablePast?: boolean
  helperText?: string
  readOnly?: boolean
  required?: boolean
  maxDate?: string
  minDate?: string
  value: Dayjs | null
  variant?: TextFieldVariants
  onChange: (value: Dayjs | null) => unknown
  onBlur?: () => unknown
  error?: boolean
  clearable?: boolean
}
export const Date = (props: DateProps) => {
  const readOnly = props.readOnly === undefined ? false : props.readOnly
  const required = props.required === undefined ? false : props.required
  const maxDate = props.maxDate === undefined ? null : props.maxDate
  const minDate = props.minDate === undefined ? null : props.minDate
  const clearable = props.clearable ?? true

  const maxDateObj = useMemo(() => (maxDate ? dayjs(maxDate) : undefined), [maxDate])
  const minDateObj = useMemo(() => (minDate ? dayjs(minDate) : undefined), [minDate])

  const onLocalChange = (value: Dayjs | null) => {
    props.onChange(value)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        format="DD-MM-YYYY"
        value={props.value === undefined ? null : props.value}
        readOnly={readOnly}
        label={props.label}
        maxDate={maxDateObj}
        minDate={minDateObj}
        onChange={onLocalChange}
        disablePast={props.disablePast}
        slotProps={{
          textField: {
            helperText: props.helperText,
            error: !!props.error,
            fullWidth: true,
            required: required,
            onBlur: props.onBlur,
            variant: props.variant || 'standard',
          },
          inputAdornment: {
            onBlur: props.onBlur,
          },
          field: {
            clearable: clearable,
          },
        }}
      />
    </LocalizationProvider>
  )
}

type CDateProps = Omit<DateProps, 'value' | 'onChange' | 'onBlur' | 'error'> & {
  name: string
  dependentFieldName?: string
}

export const CDate = (props: CDateProps) => {
  const { name, ...otherProps } = props
  const { field, fieldState } = useController({ name })
  const { trigger } = useFormContext()

  const onLocalChange = (value: Dayjs | null) => {
    field.onChange(value)

    if (props.dependentFieldName) {
      trigger(props.dependentFieldName)
    }
  }

  const safeValue = field.value ? (dayjs.isDayjs(field.value) ? field.value : dayjs(field.value)) : null

  return (
    <Date
      {...otherProps}
      onChange={onLocalChange}
      onBlur={field.onBlur}
      value={safeValue}
      error={!!fieldState.error}
      helperText={fieldState.error ? fieldState.error.message : props.helperText}
    />
  )
}
